// The datasheet template key held in DSIndex.DS_Index: <form>_<IndexSheet>_<rev>,
// e.g. 20C2042_CV-BRY-005-001-FL_00. DS_Name holds the plain form (20C2042); DS_Index adds the
// Index Sheet the copy belongs to and its revision.
//
// A plain master form carries the placeholder code instead of an Index Sheet:
// 20C2042_ISA-MFG-GRP-NUM-FP_00. The form ends at the FIRST underscore and the rev starts at the
// LAST, so Index Sheet codes may contain underscores and spaces.
//
// Revisions run 00..99, then A0..A9, AA..AZ, B0..ZZ (1,036 in all) and sort as plain strings.
//
// Mirrored on the server by PMD-API/PMD.API/Services/DsIndexName.cs. Change both.

export const PLACEHOLDER = 'ISA-MFG-GRP-NUM-FP';
export const FIRST_REV = '00';
export const MAX_FORM_LENGTH = 9;
export const MAX_LENGTH = 50;

const REV_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const PATTERN = /^([^_]{1,9})_(.+)_([0-9]{2}|[A-Z][0-9A-Z])$/;
const REV_PATTERN = /^([0-9]{2}|[A-Z][0-9A-Z])$/;

// { form, code, rev, isPlain, family } or null - null for legacy {name}-{yyyyMMdd}{seq} keys too.
export function parseDsIndex(dsIndex) {
  const m = PATTERN.exec(dsIndex || '');
  if (!m) return null;
  const [, form, code, rev] = m;
  return { form, code, rev, isPlain: code === PLACEHOLDER, family: `${form}_${code}` };
}

export const isValidRev = rev => REV_PATTERN.test(rev || '');

// The DS_Name rule; null when valid, otherwise the message to show.
export function formNameError(name) {
  if (!name || !name.trim()) return 'Datasheet name is required.';
  if (name.length > MAX_FORM_LENGTH) return `Datasheet name must be ${MAX_FORM_LENGTH} characters or fewer.`;
  if (name.includes('_')) return 'Datasheet name cannot contain an underscore.';
  return null;
}

// A blank code means a plain master form and uses the placeholder. Throws when a part is invalid
// or the key would not fit DS_Index.
export function buildDsIndex(form, code, rev) {
  const formError = formNameError(form);
  if (formError) throw new Error(formError);
  if (!isValidRev(rev)) throw new Error(`'${rev}' is not a revision (00-99, then A0-ZZ).`);
  const key = `${form}_${code && code.trim() ? code.trim() : PLACEHOLDER}_${rev}`;
  if (key.length > MAX_LENGTH) throw new Error(`'${key}' is ${key.length} characters; the limit is ${MAX_LENGTH}.`);
  return key;
}

// 00..99, A0..A9, AA..AZ, B0..ZZ. Throws after ZZ.
export function nextRev(rev) {
  if (!isValidRev(rev)) throw new Error(`'${rev}' is not a revision (00-99, then A0-ZZ).`);
  if (/[0-9]/.test(rev[0])) {
    const n = Number(rev);
    return n < 99 ? String(n + 1).padStart(2, '0') : 'A0';
  }
  const i = REV_CHARS.indexOf(rev[1]);
  if (i < REV_CHARS.length - 1) return rev[0] + REV_CHARS[i + 1];
  if (rev[0] < 'Z') return String.fromCharCode(rev.charCodeAt(0) + 1) + '0';
  throw new Error('ZZ is the last revision; there is no revision after it.');
}
